import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Lock, Upload, Palette, MessageSquare, Type, Image as ImageIcon, Sparkles, Loader2, Rocket, ArrowRight, Clock, X } from 'lucide-react';
import PortalBrandPreview from './PortalBrandPreview';

/**
 * Tiered Studio Branding — dashboard panel.
 *   Starter    → locked (upgrade banner)
 *   Pro        → accent + welcome (logo/name locked) + 7-day Ultimate trial CTA
 *   Ultimate*  → all four fields (logo + brand name + accent + greeting)
 * Includes an always-visible live portal preview wired to form state.
 */
export default function BrandSettings() {
    const [state, setState] = useState(null);
    const [form, setForm] = useState({ brand_name_string: '', brand_accent_hex: '#FF6B35', brand_welcome_message: '' });
    const [savingText, setSavingText] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [startingTrial, setStartingTrial] = useState(false);
    const [showExpiredModal, setShowExpiredModal] = useState(false);
    const logoInput = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get('/settings/branding');
            setState(data);
            const b = data.branding || {};
            setForm({
                brand_name_string: b.brand_name_string || '',
                brand_accent_hex: b.brand_accent_hex || '#FF6B35',
                brand_welcome_message: b.brand_welcome_message || '',
            });
            // Trial expiration intercept — fires once per session.
            if (data.trial_expired && !sessionStorage.getItem('mdp_trial_expired_seen')) {
                setShowExpiredModal(true);
                sessionStorage.setItem('mdp_trial_expired_seen', '1');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load branding settings');
        }
    };

    useEffect(() => { load(); }, []);

    if (!state) {
        return (
            <Card className="p-8 bg-card border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading branding settings…
                </div>
            </Card>
        );
    }

    // ---- Starter (no active trial): fully locked ----
    if (state.tier_locked) {
        return (
            <>
                <Card
                    data-testid="brand-settings-locked"
                    className="p-8 bg-card border border-[#FF6B35]/40 relative overflow-hidden"
                >
                    <div className="absolute -top-6 -right-6 h-24 w-24 border border-[#FF6B35]/20 rotate-45" aria-hidden />
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 grid place-items-center border border-[#FF6B35]/40 bg-[#FF6B35]/5 shrink-0">
                            <Lock className="h-4 w-4 text-[#FF6B35]" />
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">Locked — upgrade required</p>
                            <h3 className="mt-2 font-heading text-2xl">Studio branding is a Pro & Ultimate feature.</h3>
                            <p className="mt-3 text-sm text-muted-foreground max-w-xl leading-relaxed">
                                Upgrade to Pro to unlock custom accent color + welcome greeting for every portal you share. Upgrade to an Ultimate tier for the full white-label effect: your studio logo + custom brand name replace ours across the entire client-facing view.
                            </p>
                            <Button
                                data-testid="brand-upgrade-btn"
                                className="mt-6 bg-[#FF6B35] hover:bg-[#E55C2A] text-white"
                                onClick={() => { window.location.href = '/pricing'; }}
                            >
                                View upgrade options
                            </Button>
                        </div>
                    </div>
                </Card>
                {showExpiredModal && <TrialExpiredModal onClose={() => setShowExpiredModal(false)} />}
            </>
        );
    }

    // ---- Pro or Ultimate (or active trial) ----
    const saveTextFields = async () => {
        setSavingText(true);
        try {
            const payload = {
                brand_accent_hex: form.brand_accent_hex,
                brand_welcome_message: form.brand_welcome_message,
            };
            if (state.can_edit_logo_and_brand_name) {
                payload.brand_name_string = form.brand_name_string;
            }
            const { data } = await api.put('/settings/branding', payload);
            toast.success('Branding saved');
            setState((s) => ({ ...s, branding: data.branding }));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSavingText(false);
        }
    };

    const onLogoPick = async (file) => {
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
            toast.error('Only PNG or JPEG accepted');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Logo must be under 5MB');
            return;
        }
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/settings/branding/logo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Logo uploaded');
            setState((s) => ({
                ...s,
                branding: { ...(s.branding || {}), brand_logo_url: data.brand_logo_url },
            }));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally {
            setUploadingLogo(false);
            if (logoInput.current) logoInput.current.value = '';
        }
    };

    const startTrial = async () => {
        setStartingTrial(true);
        try {
            await api.post('/settings/branding/trial/start');
            toast.success('7-day Ultimate branding trial activated');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start trial');
        } finally {
            setStartingTrial(false);
        }
    };

    // Show trial CTA to Pro users who have never used it & aren't already Ultimate.
    const canOfferTrial = state.plan_id === 'pro' && !state.trial_active && !state.trial_used;
    const trialDaysRemaining = state.trial_active ? daysUntil(state.trial_expires_at) : 0;

    return (
        <>
            {/* ---- Trial CTA / Trial banner ---- */}
            {canOfferTrial && (
                <TrialCta onStart={startTrial} loading={startingTrial} />
            )}
            {state.trial_active && (
                <TrialActiveBanner daysRemaining={trialDaysRemaining} expiresAt={state.trial_expires_at} />
            )}

            <Card
                data-testid="brand-settings-form"
                className="p-8 bg-card border border-border relative"
            >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35] flex items-center gap-2">
                            <Sparkles className="h-3 w-3" /> Studio branding
                        </p>
                        <h3 className="mt-2 font-heading text-2xl">Your Pipeline. Your Brand.</h3>
                        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                            Personalize how the client-facing portal looks. All changes apply instantly to every active portal you share.
                        </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#FF6B35]/40 text-[#FF6B35]">
                        {state.can_edit_logo_and_brand_name ? 'Ultimate — full white-label' : 'Pro — accent & greeting'}
                    </span>
                </div>

                <div className="mt-8 grid lg:grid-cols-5 gap-8">
                    {/* --- LEFT: Form fields --- */}
                    <div className="lg:col-span-3 space-y-6 border-r-0 lg:border-r border-border lg:pr-8">
                        {/* Logo + Brand Name (Ultimate) */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-sm">
                                    <ImageIcon className="h-4 w-4 text-[#FF6B35]" />
                                    Studio Logo Asset
                                    {!state.can_edit_logo_and_brand_name && (
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-1.5 py-0.5">Ultimate</span>
                                    )}
                                </Label>
                                {state.can_edit_logo_and_brand_name ? (
                                    <>
                                        <div
                                            data-testid="brand-logo-picker"
                                            onClick={() => logoInput.current?.click()}
                                            className="h-24 border border-dashed border-border grid place-items-center bg-background/50 hover:border-[#FF6B35]/60 cursor-pointer transition-colors"
                                        >
                                            {state.branding?.brand_logo_url ? (
                                                <img
                                                    data-testid="brand-logo-preview"
                                                    src={state.branding.brand_logo_url}
                                                    alt="Studio logo"
                                                    className="h-14 max-w-[70%] object-contain"
                                                />
                                            ) : (
                                                <div className="text-center text-xs text-muted-foreground">
                                                    <Upload className="h-4 w-4 mx-auto mb-1 text-[#FF6B35]" />
                                                    Drop or click to upload PNG / JPEG (max 5MB)
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            ref={logoInput}
                                            data-testid="brand-logo-input"
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            className="hidden"
                                            onChange={(e) => onLogoPick(e.target.files?.[0])}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            {uploadingLogo ? 'Uploading…' : 'PNG or JPEG · max 5MB · stored on secure R2 partition'}
                                        </p>
                                    </>
                                ) : (
                                    <LockedField
                                        testId="brand-logo-locked"
                                        hint="Unlock White-Label Logo & Name Customization on Ultimate Tiers"
                                    />
                                )}
                            </div>

                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-sm">
                                    <Type className="h-4 w-4 text-[#FF6B35]" />
                                    Custom Studio Brand Name
                                    {!state.can_edit_logo_and_brand_name && (
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-1.5 py-0.5">Ultimate</span>
                                    )}
                                </Label>
                                {state.can_edit_logo_and_brand_name ? (
                                    <Input
                                        data-testid="brand-name-input"
                                        placeholder="e.g. Lumen Studio"
                                        value={form.brand_name_string}
                                        maxLength={80}
                                        onChange={(e) => setForm((f) => ({ ...f, brand_name_string: e.target.value }))}
                                    />
                                ) : (
                                    <LockedField
                                        testId="brand-name-locked"
                                        hint="Unlock White-Label Logo & Name Customization on Ultimate Tiers"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Accent + Greeting */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-sm">
                                    <Palette className="h-4 w-4 text-[#FF6B35]" />
                                    Interface Accent Color
                                </Label>
                                <div className="flex items-center gap-3">
                                    <input
                                        data-testid="brand-accent-picker"
                                        type="color"
                                        value={form.brand_accent_hex || '#FF6B35'}
                                        onChange={(e) => setForm((f) => ({ ...f, brand_accent_hex: e.target.value.toUpperCase() }))}
                                        className="h-10 w-14 border border-border cursor-pointer bg-transparent"
                                        aria-label="Pick accent color"
                                    />
                                    <Input
                                        data-testid="brand-accent-hex-input"
                                        value={form.brand_accent_hex}
                                        maxLength={7}
                                        placeholder="#FF6B35"
                                        onChange={(e) => setForm((f) => ({ ...f, brand_accent_hex: e.target.value.toUpperCase() }))}
                                        className="font-mono flex-1"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">Applied to buttons, drop-zones, progress bars, and outlines on your portal.</p>
                            </div>

                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-sm">
                                    <MessageSquare className="h-4 w-4 text-[#FF6B35]" />
                                    Custom Welcome Greeting
                                </Label>
                                <Textarea
                                    data-testid="brand-welcome-input"
                                    rows={3}
                                    placeholder="Drop the raw footage — thanks, team!"
                                    value={form.brand_welcome_message}
                                    maxLength={500}
                                    onChange={(e) => setForm((f) => ({ ...f, brand_welcome_message: e.target.value }))}
                                />
                                <p className="text-xs text-muted-foreground">
                                    {form.brand_welcome_message.length} / 500
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <Button
                                data-testid="brand-save-btn"
                                onClick={saveTextFields}
                                disabled={savingText}
                                className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white"
                            >
                                {savingText ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>) : 'Save branding'}
                            </Button>
                            <p className="text-xs text-muted-foreground">Changes apply to every portal instantly.</p>
                        </div>
                    </div>

                    {/* --- RIGHT: Live preview sandbox --- */}
                    <div className="lg:col-span-2 space-y-3">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-l-2 border-[#FF6B35] pl-3">
                            Live preview · updates on every keystroke
                        </p>
                        <PortalBrandPreview
                            testId="brand-live-preview"
                            accent={form.brand_accent_hex || '#FF6B35'}
                            logoUrl={state.branding?.brand_logo_url}
                            brandName={form.brand_name_string}
                            welcomeMessage={form.brand_welcome_message}
                            canWhiteLabel={state.can_edit_logo_and_brand_name}
                            canGreeting={state.can_edit_color_and_greeting}
                            portalTitle="Send us your files"
                            ownerName="Your workspace"
                        />
                    </div>
                </div>
            </Card>

            {showExpiredModal && <TrialExpiredModal onClose={() => setShowExpiredModal(false)} />}
        </>
    );
}

function daysUntil(iso) {
    if (!iso) return 0;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function LockedField({ hint, testId }) {
    return (
        <div
            data-testid={testId}
            className="border border-dashed border-border bg-background/30 p-4 flex items-center gap-3"
        >
            <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>
        </div>
    );
}

function TrialCta({ onStart, loading }) {
    return (
        <Card
            data-testid="ultimate-trial-cta"
            className="p-6 mb-6 border border-[#FF6B35]/50 bg-gradient-to-r from-[#FF6B35]/5 via-transparent to-transparent relative overflow-hidden"
        >
            <div className="absolute -right-8 -top-8 h-32 w-32 border border-[#FF6B35]/20 rotate-45" aria-hidden />
            <div className="flex items-start gap-4">
                <div className="h-11 w-11 grid place-items-center bg-[#FF6B35] text-white shrink-0">
                    <Rocket className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B35]">Growth hook · one-time</p>
                    <h4 className="mt-1 font-heading text-xl">Try Ultimate Branding for 7 days.</h4>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                        Unlock full white-label: your custom logo, studio brand-name swap, and per-portal branding overrides — instantly, at zero cost, for a week. Perfect for your next client pitch.
                    </p>
                </div>
                <Button
                    data-testid="start-ultimate-trial-btn"
                    onClick={onStart}
                    disabled={loading}
                    className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white shrink-0"
                >
                    {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Activating…</>) : (<>Start free trial <ArrowRight className="ml-2 h-4 w-4" /></>)}
                </Button>
            </div>
        </Card>
    );
}

function TrialActiveBanner({ daysRemaining, expiresAt }) {
    return (
        <div
            data-testid="ultimate-trial-active-banner"
            className="mb-6 border border-[#FF6B35]/50 bg-[#FF6B35]/5 p-4 flex items-center gap-3"
        >
            <Clock className="h-4 w-4 text-[#FF6B35] shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-sm">
                    <span className="font-medium">Ultimate branding trial active</span>
                    <span className="text-muted-foreground"> — {daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining · expires {new Date(expiresAt).toLocaleDateString()}</span>
                </p>
            </div>
            <Button
                data-testid="trial-upgrade-btn"
                variant="outline"
                size="sm"
                className="border-[#FF6B35]/50 text-[#FF6B35] hover:bg-[#FF6B35]/10 hover:text-[#FF6B35]"
                onClick={() => { window.location.href = '/pricing'; }}
            >
                Lock in Ultimate
            </Button>
        </div>
    );
}

function TrialExpiredModal({ onClose }) {
    return (
        <div
            data-testid="trial-expired-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in-0"
        >
            <div className="w-full max-w-md bg-[#0E1117] text-neutral-100 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.7)] border border-[#FF6B35]/50 relative">
                <button
                    data-testid="trial-expired-close"
                    onClick={onClose}
                    aria-label="Dismiss"
                    className="absolute top-3 right-3 text-neutral-500 hover:text-white"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="p-6 border-b border-neutral-800 flex items-start gap-3">
                    <div className="h-10 w-10 grid place-items-center bg-[#FF6B35]/10 border border-[#FF6B35]/40 shrink-0">
                        <Clock className="h-5 w-5 text-[#FF6B35]" strokeWidth={2} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF6B35]">Trial ended</p>
                        <h2 className="mt-1 font-heading text-xl font-medium tracking-tight">
                            Your 7-Day Ultimate Branding Trial Has Ended
                        </h2>
                    </div>
                </div>
                <div className="p-6 space-y-3">
                    <p className="text-sm text-neutral-300 leading-relaxed">
                        Your portals have safely reverted to standard layouts — no client uploads were interrupted, and every workspace baseline setting you configured is still saved and ready for the moment you upgrade.
                    </p>
                    <p className="text-xs text-neutral-400 leading-relaxed">
                        Upgrade to an Ultimate tier to resume full white-label branding, per-client portal overrides, and the real-time preview sandbox.
                    </p>
                </div>
                <div className="p-6 border-t border-neutral-800 flex flex-col sm:flex-row-reverse gap-3">
                    <Button
                        data-testid="trial-expired-upgrade-btn"
                        onClick={() => { window.location.href = '/pricing'; }}
                        className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white flex-1 sm:flex-initial"
                    >
                        Upgrade to Ultimate <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                        data-testid="trial-expired-dismiss-btn"
                        onClick={onClose}
                        variant="outline"
                        className="border-neutral-700 text-neutral-300 hover:text-white flex-1 sm:flex-initial"
                    >
                        Not now
                    </Button>
                </div>
            </div>
        </div>
    );
}
